// Panggung 3D mini game Sopir (Three.js): jalan layang aspal berkelok di atas hutan berkabut, bus PO Sumber Jawsel,
// penumpang, bekas ban (ribbon dinamis), asap drift. Kamera perspektif dari belakang-atas mengikuti bus.
// Pemetaan grid → dunia: (gx, gy) → (X = gy, Y = 0, Z = gx) sehingga arah +x (tahan) tampak ke kanan, +y (lepas) ke kiri.
import * as THREE from "three";
import { BUS, TEBAL, LANTAI, lebar, JENIS } from "./sopirDunia";

const KE_DUNIA = (gx, gy, y = 0) => new THREE.Vector3(gy, y, gx);
const MAJU = new THREE.Vector3(Math.SQRT1_2, 0, Math.SQRT1_2);   // arah "lurus" (diagonal x = y)
const MAKS_JEJAK = 1400;                                             // kuad per roda (ring buffer)
const WARNA_POHON = [[0x2f6a30, 0x3f8440], [0x3c7d3e, 0x4f9a50], [0x27582a, 0x366e38]];

export function buatPanggung(kanvas) {
  const renderer = new THREE.WebGLRenderer({ canvas: kanvas, antialias: true, alpha: false, powerPreference: "high-performance" });
  renderer.setPixelRatio(Math.min(2, window.devicePixelRatio || 1));
  const scene = new THREE.Scene();
  scene.background = new THREE.Color(0xb9d6cf);
  scene.fog = new THREE.Fog(0xb9d6cf, 20, 62);
  const camera = new THREE.PerspectiveCamera(58, 1, 0.1, 200);
  scene.add(new THREE.HemisphereLight(0xffffff, 0x5a7a50, 1.05));
  const matahari = new THREE.DirectionalLight(0xffffff, 1.1); matahari.position.set(-6, 14, 4); scene.add(matahari);

  // lantai hutan jauh di bawah (mengikuti kamera) + pohon instanced
  const lantai = new THREE.Mesh(new THREE.PlaneGeometry(260, 260), new THREE.MeshLambertMaterial({ color: 0x24502c }));
  lantai.rotation.x = -Math.PI / 2; lantai.position.y = -LANTAI; scene.add(lantai);
  const KAP = 2200, pohonMesh = [];
  for (const [gelap] of WARNA_POHON) {
    const tajuk = new THREE.InstancedMesh(new THREE.ConeGeometry(1, 2.6, 7), new THREE.MeshLambertMaterial({ color: gelap }), KAP);
    tajuk.count = 0; tajuk.frustumCulled = false; scene.add(tajuk); pohonMesh.push(tajuk);
  }
  const pohonIdx = [0, 0, 0];
  const M = new THREE.Matrix4(), Q = new THREE.Quaternion(), V = new THREE.Vector3(), S = new THREE.Vector3();

  // bahan jalan: atas aspal, sisi gelap
  const matAtas = new THREE.MeshLambertMaterial({ color: 0x8a9199 }), matSisi = new THREE.MeshLambertMaterial({ color: 0x3a3f47 });
  const matJalan = [matSisi, matSisi, matAtas, matSisi, matSisi, matSisi];
  const jalanMesh = new Map();   // indeks segmen → mesh

  const bus = buatBus(); scene.add(bus);
  const penumpangMesh = new Map(), rintanganMesh = new Map();
  const matBatu = new THREE.MeshLambertMaterial({ color: 0x7d7f84 }), matKayu = new THREE.MeshLambertMaterial({ color: 0x6b4423 }), matLubang = new THREE.MeshBasicMaterial({ color: 0x1b1d21 });
  const geoBatu = new THREE.DodecahedronGeometry(0.42, 0), geoKayu = new THREE.CylinderGeometry(0.22, 0.22, 1.6, 8), geoLubang = new THREE.CircleGeometry(0.5, 14);
  function buatRintangan(r) {
    let m;
    if (r.jenis === "batu") { m = new THREE.Mesh(geoBatu, matBatu); m.position.y = 0.3; m.rotation.set(r.putar, r.putar * 0.7, 0); }
    else if (r.jenis === "kayu") { m = new THREE.Mesh(geoKayu, matKayu); m.position.y = 0.22; m.rotation.set(0, r.putar, Math.PI / 2); }
    else { m = new THREE.Mesh(geoLubang, matLubang); m.rotation.x = -Math.PI / 2; m.position.y = 0.015; }
    const g = new THREE.Group(); g.add(m); g.position.copy(KE_DUNIA(r.x, r.y)); return g;
  }
  const labelTex = new Map();

  // bekas ban: dua ribbon (kiri/kanan), ring buffer kuad non-indeks
  const jejak = ["ki", "ka"].map(() => {
    const g = new THREE.BufferGeometry(), pos = new Float32Array(MAKS_JEJAK * 6 * 3);
    g.setAttribute("position", new THREE.BufferAttribute(pos, 3)); g.setDrawRange(0, 0);
    const m = new THREE.Mesh(g, new THREE.MeshBasicMaterial({ color: 0x0e0f11, transparent: true, opacity: 0.92, depthWrite: false, polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -2 }));
    m.frustumCulled = false; m.renderOrder = 1; scene.add(m);
    return { g, pos, n: 0, prev: null, m };
  });
  let jejakDibaca = 0;

  const asapGeo = new THREE.SphereGeometry(1, 8, 6), asapMat = new THREE.MeshBasicMaterial({ color: 0xe9ecef, transparent: true, opacity: 0.45, depthWrite: false });
  const asapPool = Array.from({ length: 40 }, () => { const m = new THREE.Mesh(asapGeo, asapMat.clone()); m.visible = false; scene.add(m); return m; });

  function buatBus() {
    const { L, W, H } = BUS, g = new THREE.Group();
    const putih = new THREE.MeshLambertMaterial({ color: 0xf4f6f8 }), biru = new THREE.MeshLambertMaterial({ color: 0x2e58c4 }), merah = new THREE.MeshLambertMaterial({ color: 0xc62828 });
    const kaca = new THREE.MeshLambertMaterial({ color: 0x1f3a66 }), hitam = new THREE.MeshLambertMaterial({ color: 0x141414 }), abu = new THREE.MeshLambertMaterial({ color: 0xcfd4da });
    const kotak = (w, h, d, mat, x, y, z) => { const m = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), mat); m.position.set(x, y, z); g.add(m); return m; };
    kotak(W, H - 0.16, L, putih, 0, 0.16 + (H - 0.16) / 2, 0);                 // badan
    kotak(W + 0.02, 0.2, L * 0.86, kaca, 0, H - 0.24, -0.02);                   // jendela samping
    kotak(W * 0.9, 0.2, 0.04, kaca, 0, H - 0.24, L / 2 + 0.005);                // kaca depan
    kotak(W * 0.8, 0.18, 0.04, kaca, 0, H - 0.26, -L / 2 - 0.005);              // kaca belakang
    kotak(W + 0.03, 0.08, L, biru, 0, 0.36, 0); kotak(W + 0.03, 0.03, L, merah, 0, 0.3, 0);   // livery
    kotak(0.2, 0.08, L * 0.36, abu, -0.14, H + 0.04, 0.15); kotak(0.2, 0.08, L * 0.36, abu, 0.14, H + 0.04, -0.25);   // AC atap
    kotak(0.12, 0.06, 0.03, merah, -W / 2 + 0.1, 0.24, -L / 2 - 0.01); kotak(0.12, 0.06, 0.03, merah, W / 2 - 0.1, 0.24, -L / 2 - 0.01);  // lampu belakang
    const roda = new THREE.CylinderGeometry(0.12, 0.12, 0.1, 10);
    for (const [x, z] of [[-W / 2, L * 0.32], [W / 2, L * 0.32], [-W / 2, -L * 0.32], [W / 2, -L * 0.32]]) { const m = new THREE.Mesh(roda, hitam); m.rotation.z = Math.PI / 2; m.position.set(x, 0.12, z); g.add(m); }
    return g;
  }

  function buatPenumpang(p) {
    const j = JENIS[p.jenis], g = new THREE.Group();
    const mat = c => new THREE.MeshLambertMaterial({ color: new THREE.Color(c) });
    const kotak = (w, h, d, m, y) => { const b = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), m); b.position.y = y; g.add(b); };
    kotak(0.34, 0.36, 0.22, mat(j.celana), 0.18); kotak(0.4, 0.42, 0.26, mat(j.baju), 0.57);
    const kepala = new THREE.Mesh(new THREE.SphereGeometry(0.16, 10, 8), mat("#f1c9a5")); kepala.position.y = 0.94; g.add(kepala);
    const topi = j.jilbab || j.topi || j.helm || j.blangkon || "#141414";
    const rambut = new THREE.Mesh(new THREE.SphereGeometry(0.17, 10, 8, 0, Math.PI * 2, 0, Math.PI / 2), mat(topi)); rambut.position.y = 0.95; g.add(rambut);
    if (j.keranjang) kotak(0.2, 0.18, 0.2, mat(j.keranjang), 0.42);
    if (!labelTex.has(p.tarif)) {
      const c = document.createElement("canvas"); c.width = 128; c.height = 64; const x = c.getContext("2d");
      x.fillStyle = "rgba(255,255,255,.95)"; x.beginPath(); x.roundRect(4, 6, 120, 52, 26); x.fill();
      x.fillStyle = "#141414"; x.font = "800 34px system-ui, sans-serif"; x.textAlign = "center"; x.textBaseline = "middle"; x.fillText(`+${p.tarif}`, 64, 34);
      const tex = new THREE.CanvasTexture(c); tex.colorSpace = THREE.SRGBColorSpace; labelTex.set(p.tarif, tex);
    }
    const sp = new THREE.Sprite(new THREE.SpriteMaterial({ map: labelTex.get(p.tarif), depthTest: false })); sp.scale.set(0.9, 0.45, 1); sp.position.y = 1.45; g.add(sp);
    g.position.copy(KE_DUNIA(p.x, p.y)); g.rotation.y = Math.PI / 4 + Math.PI;   // menghadap bus yang datang
    return g;
  }

  // Segmen: kotak dari (a − w/2) hingga (b − w_berikut/2) agar tidak tumpang-tindih dengan sudut milik segmen berikutnya
  function buatJalan(seg) {
    const { a, b, arah, w } = seg;
    if (arah === "d") {
      const len = Math.hypot(b.x - a.x, b.y - a.y) + w / 2, m = new THREE.Mesh(new THREE.BoxGeometry(w, TEBAL, len), matJalan);
      const tengah = KE_DUNIA((a.x + b.x) / 2 - Math.SQRT1_2 * w / 4, (a.y + b.y) / 2 - Math.SQRT1_2 * w / 4, -TEBAL / 2 - 0.004);
      m.position.copy(tengah); m.rotation.y = Math.PI / 4; return m;
    }
    const wB = lebar(b.x + b.y), u0 = a[arah] - w / 2, u1 = b[arah] - wB / 2, len = Math.max(0.05, u1 - u0), tengah = (u0 + u1) / 2;
    const m = arah === "x" ? new THREE.Mesh(new THREE.BoxGeometry(w, TEBAL, len), matJalan) : new THREE.Mesh(new THREE.BoxGeometry(len, TEBAL, w), matJalan);
    m.position.copy(arah === "x" ? KE_DUNIA(tengah, a.y, -TEBAL / 2) : KE_DUNIA(a.x, tengah, -TEBAL / 2));
    return m;
  }

  function tambahKuad(r, kiA, kiB, kaA, kaB) {
    const i = (r.n % MAKS_JEJAK) * 18, p = r.pos;
    const tulis = (o, v) => { p[o] = v.x; p[o + 1] = v.y; p[o + 2] = v.z; };
    tulis(i, kiA); tulis(i + 3, kaA); tulis(i + 6, kiB); tulis(i + 9, kaA); tulis(i + 12, kaB); tulis(i + 15, kiB);
    r.n++; r.g.setDrawRange(0, Math.min(r.n, MAKS_JEJAK) * 6); r.g.attributes.position.needsUpdate = true;
  }
  // ribbon selebar 0.16 di sekitar posisi roda: offset tegak lurus arah gerak roda
  function sambungJejak(r, dari, ke) {
    const A = KE_DUNIA(dari.x, dari.y, 0.012), B = KE_DUNIA(ke.x, ke.y, 0.012), d = B.clone().sub(A);
    if (d.lengthSq() < 1e-6) return;
    const n = new THREE.Vector3(-d.z, 0, d.x).normalize().multiplyScalar(0.11);
    tambahKuad(r, A.clone().sub(n), B.clone().sub(n), A.clone().add(n), B.clone().add(n));
  }

  function perbarui(s, t) {
    // jalan baru / buang yang jauh di belakang
    const D = s.pos.x + s.pos.y;
    s.segmen.forEach((seg, i) => {
      if (!jalanMesh.has(i) && seg.a.x + seg.a.y < D + 60) { const m = buatJalan(seg); scene.add(m); jalanMesh.set(i, m); }
    });
    for (const [i, m] of jalanMesh) if (s.segmen[i].b.x + s.segmen[i].b.y < D - 30) { scene.remove(m); m.geometry.dispose(); jalanMesh.delete(i); }
    // pohon baru
    for (const p of s.pohonBaru) {
      const k = p.nada, mesh = pohonMesh[k], idx = pohonIdx[k] % KAP; pohonIdx[k]++;
      const sk = p.ukuran * 0.85;
      V.set(p.y, -LANTAI + 1.3 * sk, p.x); S.set(sk, sk, sk); M.compose(V, Q.identity(), S); mesh.setMatrixAt(idx, M); mesh.count = Math.min(KAP, pohonIdx[k]); mesh.instanceMatrix.needsUpdate = true;
    }
    s.pohonBaru.length = 0;
    // penumpang
    for (const p of s.penumpang) {
      let g = penumpangMesh.get(p.id);
      if (p.lewat || (p.naik && t - p.t > 0.35) || p.x + p.y < D - 20) { if (g) { scene.remove(g); penumpangMesh.delete(p.id); } continue; }
      if (!g && p.x + p.y < D + 60) { g = buatPenumpang(p); scene.add(g); penumpangMesh.set(p.id, g); }
      if (!g) continue;
      if (p.naik) { const u = (t - p.t) / 0.35; g.position.y = u * 1.6; g.scale.setScalar(Math.max(0.01, 1 - u)); }
      else g.position.y = Math.abs(Math.sin(t * 6 + p.id)) * 0.08;
    }
    // rintangan: batu/kayu terpental saat ditabrak, lubang tetap
    for (const r of s.rintangan) {
      let g = rintanganMesh.get(r.id);
      const terpental = r.kena && r.jenis !== "lubang";
      if ((terpental && t - r.t > 0.6) || r.x + r.y < D - 20) { if (g) { scene.remove(g); rintanganMesh.delete(r.id); } continue; }
      if (!g && r.x + r.y < D + 60) { g = buatRintangan(r); scene.add(g); rintanganMesh.set(r.id, g); }
      if (g && terpental) { const u = (t - r.t) / 0.6; g.position.y = 2.2 * u - 2.6 * u * u; g.position.x += 0.06; g.rotation.x += 0.2; g.scale.setScalar(Math.max(0.01, 1 - u * 0.6)); }
    }
    // bus
    bus.position.copy(KE_DUNIA(s.pos.x, s.pos.y));
    // body roll: miring ke luar tikungan sebanding sudut selip (+ sedikit dari laju putar), hidung menunduk saat drift
    const selip = s.thB - s.th, rollTarget = THREE.MathUtils.clamp(-(selip * 0.75 + s.wB * 0.04), -0.38, 0.38);
    s.roll += (rollTarget - s.roll) * 0.18;
    bus.rotation.set(Math.abs(selip) * 0.1, s.thB, s.roll, "YXZ");
    bus.position.y = Math.abs(s.roll) * 0.08;
    if (s.jatuh) { const u = s.jatuh.t; bus.position.y = -5 * u * u; bus.rotation.z = (s.arah === "x" ? 1 : -1) * Math.min(1.2, u * 1.6); bus.rotation.x = u * 0.8; }
    // bekas ban: sambungkan sampel baru; saat drift putus-putus (jeda tiap ~90 ms)
    for (let i = Math.max(0, s.jejak.length - (s.jejakN - jejakDibaca)); i < s.jejak.length; i++) {
      const j = s.jejak[i], pen = !j.drift || Math.floor(j.t / 0.09) % 2 === 0;
      jejak.forEach((r, k) => { const titik = k === 0 ? j.ki : j.ka; if (r.prev && pen) sambungJejak(r, r.prev, titik); r.prev = titik; });
    }
    jejakDibaca = s.jejakN;
    // asap
    asapPool.forEach((m, i) => { const a = s.asap[i]; if (!a) { m.visible = false; return; } m.visible = true; m.position.copy(KE_DUNIA(a.x, a.y, 0.2 + a.t * 0.6)); const r = a.r * (1 + a.t * 2.5); m.scale.setScalar(r); m.material.opacity = 0.45 * (1 - a.t / 0.7); });
    // kamera: di belakang-atas bus (arah lurus diagonal), melihat ke depan
    const target = KE_DUNIA(s.cam.x, s.cam.y);
    camera.position.copy(target).addScaledVector(MAJU, -10.5).setY(13.5);
    camera.lookAt(target.x + MAJU.x * 0.5, 0, target.z + MAJU.z * 0.5);
    lantai.position.x = target.x; lantai.position.z = target.z;
    renderer.render(scene, camera);
  }

  function ukur() {
    const w = kanvas.clientWidth, h = kanvas.clientHeight;
    renderer.setSize(w, h, false); camera.aspect = w / h; camera.fov = h > w ? 56 : 44; camera.updateProjectionMatrix();
  }
  function buang() { renderer.dispose(); renderer.forceContextLoss(); }
  return { perbarui, ukur, buang };
}
